// A dialog moved by dragging its title bar (the header). It stays inside the window and opens
// centred: the offset lives with the dialog, so a dialog opened again starts at the middle. The
// platform's Dialog and AlertDialog take a `ref` and a `style` on their popup, so this is the app's.
//
// Every dialog opens with the focus on itself (`initialFocus={drag.ref}`) or on a field or Cancel
// it names — never on a button that saves: Base UI focuses the first button otherwise, and the Enter
// or Space that comes next would press it. Esc still closes it. `routing-checks.ts` checks each.

import { useCallback, useRef, useState } from "react";
import type { CSSProperties, PointerEvent } from "react";

/** What a press on the title bar never moves: its buttons and fields keep their clicks. */
const CONTROLS = "button, input, select, textarea, a, [role=combobox], [role=checkbox]";

const clamp = (v: number, lo: number, hi: number) => (hi < lo ? lo : Math.min(hi, Math.max(lo, v)));

export function useDialogDrag() {
  const popup = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const reset = useCallback(() => setOffset({ x: 0, y: 0 }), []);
  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest(CONTROLS)) return;
    const el = popup.current;
    if (!el) return;
    // No text selected and the focus left where it is (a field keeps it).
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const start = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    const range = {
      x: [start.ox - rect.left, start.ox + window.innerWidth - rect.right] as const,
      y: [start.oy - rect.top, start.oy + window.innerHeight - rect.bottom] as const,
    };
    const move = (ev: globalThis.PointerEvent) =>
      setOffset({
        x: clamp(start.ox + ev.clientX - start.x, ...range.x),
        y: clamp(start.oy + ev.clientY - start.y, ...range.y),
      });
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };
  return {
    /** The popup's ref: its edges keep it in the window; its `initialFocus` too (the dialog itself). */
    ref: popup,
    /** The popup's style: centred (as the platform puts it), then moved. */
    style: { translate: `calc(-50% + ${offset.x}px) calc(-50% + ${offset.y}px)` } satisfies CSSProperties,
    /** The title bar's props. */
    handle: { onPointerDown, className: "cursor-move select-none touch-none" },
    /** Back to the middle (a message the provider keeps mounted, opened again). */
    reset,
  };
}
